import Debug from '../../helpers/debug.js';
import ora from 'ora';
import { readFileSync, existsSync } from 'fs';
import Table from 'cli-table';
import { pr, themed, printJSON } from '../../helpers/print.js';
import { runJob, getSupportedFlow } from '../../helpers/api.js';

const debug = (...args) => {
  const debug = Debug('commands:jobs:bulkRun');
  debug(...args);
};

const command = 'bulk-run';

const desc = 'Run jobs in bulk';

const builder = async (yargs) => {
  debug('builder', command);

  return yargs
    .option('file', {
      describe: 'Path to JSON file containing array of jobs to run',
      type: 'string',
      alias: ['f'],
      demandOption: true,
    })
    .demandOption('file', 'Path to JSON file is required. Example: -f ./jobs.json');
};

const handler = async (argv) => {
  debug('handler', command, argv);

  const { hg, json, apikey, env, file } = argv;
  const { clientApiBaseUri } = hg;

  let message;
  let spinner = ora();
  try {
    // 1. Read and validate JSON file
    message = themed(`Reading jobs file: ${themed(file, 'info')}`);
    !json && spinner.start(themed(`In progress: ${message}...`, 'secondary'));

    if (!existsSync(file)) {
      throw new Error(`File not found: ${file}`);
    }

    const fileContent = readFileSync(file, { encoding: 'utf-8' });
    const jobs = JSON.parse(fileContent);

    if (!Array.isArray(jobs)) {
      throw new Error('JSON file must contain an array of job objects');
    }

    if (jobs.length === 0) {
      json
        ? printJSON({ status: 'error', error: 'No jobs found in file' })
        : spinner.warn(themed(`Warning: ${themed('No jobs found in file')}.`, 'secondary'));
      return;
    }

    // Validate each job has required fields
    for (let i = 0; i < jobs.length; i++) {
      const job = jobs[i];
      if (!job.flow) {
        throw new Error(`Job at index ${i} is missing required field: flow`);
      }
      if (!job.tenant) {
        throw new Error(`Job at index ${i} is missing required field: tenant`);
      }
    }

    !json && spinner.succeed(themed(`Finished: ${message}.`, 'secondary'));

    // 2. Get unique flows and check if they are V2
    message = themed(`Checking flow versions...`);
    !json && spinner.start(themed(`In progress: ${message}...`, 'secondary'));

    const uniqueFlows = [...new Set(jobs.map((job) => job.flow))];
    const flowVersions = {};

    await Promise.all(
      uniqueFlows.map(async (flow) => {
        try {
          const supportedFlow = await getSupportedFlow({
            debug,
            baseUri: clientApiBaseUri,
            apikey,
            env,
            flow,
          });
          flowVersions[flow] = supportedFlow?.version === 2;
        } catch (err) {
          debug(`Error checking flow ${flow}:`, err);
          // Default to false if we can't determine
          flowVersions[flow] = false;
        }
      })
    );

    !json && spinner.succeed(themed(`Finished: ${message}.`, 'secondary'));

    // 3. Run all jobs in parallel
    message = themed(`Running ${themed(jobs.length, 'info')} job(s)...`);
    !json && spinner.start(themed(`In progress: ${message}...`, 'secondary'));

    const results = await Promise.all(
      jobs.map(async (job, index) => {
        const { flow, tenant, ...jobArgs } = job;
        const isV2Flow = flowVersions[flow] || false;

        try {
          const response = await runJob({
            debug,
            baseUri: clientApiBaseUri,
            env,
            apikey,
            tenant,
            flow,
            isV2Flow,
            ...jobArgs,
          });

          return {
            index,
            flow,
            tenant,
            ok: true,
            response,
          };
        } catch (error) {
          let errorResult;
          
          if (error.response) {
            // The request was made and the server responded with a status code
            // that falls out of the range of 2xx
            const statusCode = error.response.status;
            const statusText = error.response.statusText;
            const responseData = error.response.data;
            const message = error.message;
            
            debug('Error response:', {
              statusCode,
              statusText,
              message,
              data: responseData
            });
            
            errorResult = {
              statusCode,
              statusText,
              message,
              data: responseData
            };
          } else if (error.request) {
            // The request was made but no response was received
            debug('Error: No response received', error.message);
            errorResult = {
              message: error.message,
              request: error.request
            };
          } else {
            // Something happened in setting up the request that triggered an Error
            debug('Error:', error.message);
            errorResult = {
              message: error.message
            };
          }

          return {
            index,
            flow,
            tenant,
            ok: false,
            error: errorResult,
          };
        }
      })
    );

    !json && spinner.succeed(themed(`Finished: ${message}.`, 'secondary'));

    // 4. Print results
    if (json) {
      printJSON(results);
    } else {
      // Generate results table
      const table = new Table({
        head: ['Index', 'Flow', 'Tenant', 'Status', 'Response/Error'],
      });

      results.forEach((result) => {
        const status = result.ok ? 'SUCCESS' : 'FAILED';
        const responseOrError = result.ok
          ? JSON.stringify(result.response, undefined, 2)
          : JSON.stringify(result.error, undefined, 2);
        table.push([result.index, result.flow, result.tenant, status, responseOrError]);
      });

      console.log(table.toString());

      // Print summary
      const successCount = results.filter((r) => r.ok).length;
      const failCount = results.filter((r) => !r.ok).length;
      pr(
        themed(
          `Summary: ${themed(String(successCount), 'info')} succeeded, ${themed(String(failCount), 'error')} failed out of ${themed(
            String(results.length),
            'info'
          )} total.`,
          'secondary'
        )
      );
    }
  } catch (err) {
    if (json) {
      printJSON({ status: 'error', error: err.message, stack: err.stack });
    } else {
      spinner.fail(themed(`Error: ${message || 'Failed to run jobs'}.`, 'secondary'));
      pr(themed(`Message: ${themed(err.message)}`, 'secondary'));
      debug(err);
    }
    process.exit(1);
  }
};

export { command, desc, builder, handler };
